import React, { useState } from 'react';
import { useAuth } from '../../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { CheckCircle, X, Lock, Eye, EyeOff, ArrowLeft } from 'lucide-react';

function PasswordChange() {
    const { updatePassword, currentUser } = useAuth();
    const navigate = useNavigate();
    
    // Check if user needs to set password (OAuth user without password) vs change password
    const needsPasswordSet = currentUser?.has_password === false;
    
  
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(false);
    
    const [passwordData, setPasswordData] = useState({
        currentPassword: '',
        password: '',
        passwordConfirmation: ''
    });
    
    const [showPasswords, setShowPasswords] = useState({
        current: false,
        new: false,
        confirm: false
    });
    
    const handleChange = (e) => {
        const { name, value } = e.target;
        setPasswordData(prev => ({
            ...prev,
            [name]: value
        }));
        setError(null);
        setSuccess(false);
    };
    
    const togglePasswordVisibility = (field) => {
        setShowPasswords(prev => ({
            ...prev,
            [field]: !prev[field]
        }));
    };
    
    const handleSubmit = async () => {
        // Reset states
        setError(null);
        setSuccess(false);
        
        // Validation checks - skip current password for users setting password for first time
        if (!needsPasswordSet && !passwordData.currentPassword.trim()) {
            setError('Current password is required');
            return;
        }
        
        if (!passwordData.password.trim()) {
            setError('New password is required');
            return;
        }
        
        if (passwordData.password !== passwordData.passwordConfirmation) {
            setError('New passwords do not match');
            return;
        }
        
        if (passwordData.password.length < 8) {
            setError('Password must be at least 8 characters long');
            return;
        }
        
        setLoading(true);
        
        try {
            const result = await updatePassword({
                currentPassword: needsPasswordSet ? '' : passwordData.currentPassword,
                password: passwordData.password,
                passwordConfirmation: passwordData.passwordConfirmation
            });
            
            if (result && result.success) {
                setPasswordData({
                    currentPassword: '',
                    password: '',
                    passwordConfirmation: ''
                });
                setSuccess(true);
                // Navigate back to profile after 2 seconds
                setTimeout(() => {
                    navigate('/dashboard/profile');
                }, 2000);
            } else {
                // Handle API errors properly
                const errorMessage = result?.error || 'Failed to update password';
                setError(errorMessage);
            }
        } catch (err) {
            setError('An error occurred while updating password');
        } finally {
            setLoading(false);
        }
    };

    if (success) {
        return (
            <div className="max-w-md mx-auto mt-8">
                <div className="bg-white dark:bg-gray-800 rounded-lg p-8 shadow-sm border border-gray-200 dark:border-gray-700">
                    <div className="text-center">
                        <CheckCircle className="mx-auto h-16 w-16 text-green-500 mb-4" />
                        <h2 className="text-2xl font-unna font-semibold text-gray-900 dark:text-white mb-2">
                            {needsPasswordSet ? 'Password Set Successfully!' : 'Password Updated Successfully!'}
                        </h2>
                        <p className="text-gray-600 dark:text-gray-400 mb-4">
                            {needsPasswordSet ? 'Your password has been set. You can now sign in with your email and password. Redirecting to profile...' : 'Your password has been changed. Redirecting to profile...'}
                        </p>
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500 mx-auto"></div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="max-w-md mx-auto mt-8">
            {/* Back Button */}
            <button
                onClick={() => navigate('/dashboard/profile')}
                className="flex items-center gap-2 text-blue-600 hover:text-blue-800 mb-6 font-rasa"
            >
                <ArrowLeft size={20} />
                Back to Profile
            </button>

            <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm border border-gray-200 dark:border-gray-700">
                <div className="flex items-center gap-3 mb-6">
                    <Lock size={24} className="text-blue-600" />
                    <h2 className="text-2xl font-unna font-semibold">
                        {needsPasswordSet ? 'Set Password' : 'Change Password'}
                    </h2>
                </div>
                
                {needsPasswordSet && (
                    <div className="bg-blue-50 dark:bg-blue-900/20 p-3 rounded-lg border border-blue-200 dark:border-blue-800 text-blue-700 dark:text-blue-300 mb-4">
                        <p className="text-sm">
                            You signed up with social authentication. Set a password to also be able to sign in with your email and password.
                        </p>
                    </div>
                )}
                
                {error && (
                    <div className="bg-red-50 dark:bg-red-900/20 p-3 rounded-lg font-rasa border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 flex items-center mb-4">
                        <X size={18} className="mr-2" />
                        {error}
                    </div>
                )}
                
                <div className="space-y-4">
                    {!needsPasswordSet && (
                        <div>
                            <label className="block text-sm font-rasa font-medium mb-2">Current Password</label>
                            <div className="relative">
                                <input
                                    type={showPasswords.current ? "text" : "password"}
                                    name="currentPassword"
                                    value={passwordData.currentPassword}
                                    onChange={handleChange}
                                    className="w-full px-3 py-2 pr-10 border text-gray-900 dark:text-gray-200 rounded-lg bg-white dark:bg-gray-700 dark:border-gray-600 focus:ring-blue-500 focus:border-blue-500"
                                    required
                                    disabled={loading}
                                />
                                <button
                                    type="button"
                                    onClick={() => togglePasswordVisibility('current')}
                                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                                    disabled={loading}
                                >
                                    {showPasswords.current ? <EyeOff size={16} /> : <Eye size={16} />}
                                </button>
                            </div>
                        </div>
                    )}
                    
                    <div>
                        <label className="block text-sm font-rasa font-medium mb-2">
                            {needsPasswordSet ? 'Password' : 'New Password'}
                        </label>
                        <div className="relative">
                            <input
                                type={showPasswords.new ? "text" : "password"}
                                name="password"
                                value={passwordData.password}
                                onChange={handleChange}
                                className="w-full px-3 py-2 pr-10 border text-gray-900 dark:text-gray-200 rounded-lg bg-white dark:bg-gray-700 dark:border-gray-600 focus:ring-blue-500 focus:border-blue-500"
                                required
                                minLength={8}
                                disabled={loading}
                            />
                            <button
                                type="button"
                                onClick={() => togglePasswordVisibility('new')}
                                className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                                disabled={loading}
                            >
                                {showPasswords.new ? <EyeOff size={16} /> : <Eye size={16} />}
                            </button>
                        </div>
                        <p className="text-xs text-gray-500 mt-1">Must be at least 8 characters long</p>
                    </div>
                    
                    <div>
                        <label className="block text-sm font-rasa font-medium mb-2">
                            {needsPasswordSet ? 'Confirm Password' : 'Confirm New Password'}
                        </label>
                        <div className="relative">
                            <input
                                type={showPasswords.confirm ? "text" : "password"}
                                name="passwordConfirmation"
                                value={passwordData.passwordConfirmation}
                                onChange={handleChange}
                                className="w-full px-3 py-2 pr-10 border text-gray-900 dark:text-gray-200 rounded-lg bg-white dark:bg-gray-700 dark:border-gray-600 focus:ring-blue-500 focus:border-blue-500"
                                required
                                minLength={8}
                                disabled={loading}
                            />
                            <button
                                type="button"
                                onClick={() => togglePasswordVisibility('confirm')}
                                className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                                disabled={loading}
                            >
                                {showPasswords.confirm ? <EyeOff size={16} /> : <Eye size={16} />}
                            </button>
                        </div>
                    </div>
                    
                    <div className="flex justify-end space-x-3 pt-4">
                        <button 
                            type="button" 
                            onClick={() => navigate('/dashboard/profile')}
                            className="px-4 py-2 border text-base border-gray-300 font-rasa dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                            disabled={loading}
                        >
                            Cancel
                        </button>
                        <button 
                            type="button"
                            onClick={handleSubmit}
                            className="px-4 py-2 bg-blue-500 text-white rounded-lg font-rasa text-base hover:bg-blue-600 transition-colors disabled:opacity-50"
                            disabled={loading}
                        >
                            {loading ? (needsPasswordSet ? 'Setting...' : 'Updating...') : (needsPasswordSet ? 'Set Password' : 'Update Password')}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default PasswordChange;